import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  RequestsByEndpoint,
  RequestsOverTime,
  VizStyle,
} from "../../usage/charts";

/**
 * One client's last 30 days.
 *
 * Deliberately not a second Usage page: the question on a client's own detail is "is this
 * customer actually using what we sold them", which is answered by a shape over time and
 * the endpoints they call. Anything more specific belongs on the Usage page, which this
 * links to with the client already selected.
 */
export function UsageTab({ clientKey }: { clientKey: string }) {
  const filter = { clients: clientKey, days: 30 };

  const config = useQuery({ queryKey: ["b2b", "usage", "config"], queryFn: api.b2b.usageConfig });
  const enabled = config.data?.configured === true;

  const timeseries = useQuery({
    queryKey: ["b2b", "usage", "timeseries", filter],
    queryFn: () => api.b2b.usage("timeseries", filter),
    enabled,
  });
  const byEndpoint = useQuery({
    queryKey: ["b2b", "usage", "by-endpoint", filter],
    queryFn: () => api.b2b.usage("by-endpoint", filter),
    enabled,
  });

  const total = (timeseries.data?.rows ?? []).reduce(
    (sum, row) => sum + (typeof row.requests === "number" ? row.requests : 0),
    0
  );
  const loaded = timeseries.data !== undefined;

  return (
    <div className="space-y-4 rounded-xl border bg-card p-6">
      <VizStyle />

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-semibold">Usage — last 30 days</h2>
          <p className="text-sm text-muted-foreground">
            {loaded
              ? `${total.toLocaleString()} requests`
              : enabled
                ? "Loading…"
                : "Application Insights is not configured here"}
          </p>
        </div>
        <Button variant="secondary" size="sm" asChild>
          <Link to={`/dashboard/b2b/usage?client=${encodeURIComponent(clientKey)}&days=30`}>
            Open in Usage
          </Link>
        </Button>
      </div>

      {/* "Never used" is a real answer, not an empty state: a client with grants and no
          calls in 90 days is either mis-integrated or no longer a customer. */}
      {loaded && total === 0 && (
        <Badge variant="outline" className="text-amber-700 dark:text-amber-400">
          Never used — no requests in the retention window
        </Badge>
      )}

      {timeseries.isError && (
        <p className="text-sm text-destructive">
          {timeseries.error instanceof Error ? timeseries.error.message : "This query failed."}
        </p>
      )}

      {loaded && total > 0 && (
        <>
          <RequestsOverTime
            points={(timeseries.data?.rows ?? []).map((row) => ({
              timestamp: String(row.timestamp ?? ""),
              requests: typeof row.requests === "number" ? row.requests : 0,
            }))}
          />
          <div>
            <h3 className="mb-2 text-sm font-medium">By endpoint</h3>
            <RequestsByEndpoint
              rows={(byEndpoint.data?.rows ?? []).map((row) => ({
                name: String(row.name ?? ""),
                requests: typeof row.requests === "number" ? row.requests : 0,
                failed: typeof row.failed === "number" ? row.failed : 0,
              }))}
            />
          </div>
        </>
      )}
    </div>
  );
}
